<template>
  <v-container
    class="elevation-4"
    :style="`height: ${settingsType === 4 ? '675px' : '400px'}; width: ${settingsType === 4 ? '950px' : '600px'}; margin-top: ${settingsType === 4 ? '50px' : '100px'};`"
  >
    <v-container>
      <v-form
        @hide-form="loading = false"
        ref="form"
        @submit.prevent=""
        validate-on="input"
        v-model:model-value="valid"
      >
        <v-row justify="space-between">
          <v-col v-if="[0, 1, 2].includes(settingsType)">
            <h3>Transport-Auswahl</h3>
            <v-select
              label="Verkehrsmittel"
              :items="transports"
              item-title="name"
              item-value="id"
              v-model="firstTransport"
              :rules="rules.transport"
              v-if="[0, 1, 2].includes(settingsType)"
            ></v-select>
            <v-select
              label="Verkehrsmittel für Vergleich"
              hint="Dieses Verkehrsmittel wird Vergleichskarten verwendet"
              :items="transports"
              item-title="name"
              item-value="id"
              v-model="secondTransport"
              :rules="rules.transport"
              v-if="settingsType === 2"
            ></v-select>
            <v-select
              label="Metrik"
              :items="metrics"
              item-title="name"
              item-value="value"
              v-model="metric"
              :rules="rules.metric"
              v-if="settingsType === 2"
            ></v-select>
          </v-col>
          <v-col v-if="[3].includes(settingsType)">
            <h3>Metrik-Auswahl</h3>
            <v-select
              label="Metrik"
              :items="best_transport_metrics"
              item-title="name"
              item-value="value"
              v-model="metric"
              :rules="rules.metric"
            ></v-select>
          </v-col>
          <v-col v-if="[4].includes(settingsType)">
            <h3>Beste Alternative Einstellungen</h3>

            <!-- Current Transport Selection -->
            <v-select
              v-model="bestAlternativeSettings.current_transport"
              :items="transportModeOptions"
              item-title="text"
              item-value="value"
              label="Aktuelles Verkehrsmittel"
              :rules="rules.bestAlternativeTransport"
            ></v-select>

            <!-- Time Constraints -->
            <div class="mt-4">
              <v-slider
                v-model="bestAlternativeSettings.max_time_percentage"
                :label="`Max. Zeitaufschlag: ${bestAlternativeSettings.max_time_percentage} %`"
                min="0"
                max="100"
                step="5"
                :thumb-label="true"
                color="primary"
              ></v-slider>
            </div>

            <v-text-field
              v-model="bestAlternativeSettings.max_additional_minutes"
              label="Max. zusätzliche Minuten"
              type="number"
              min="0"
              max="120"
              suffix="min"
            ></v-text-field>

            <!-- Optimization Metric -->
            <v-select
              v-model="bestAlternativeSettings.selected_metric"
              :items="metricOptions"
              item-title="text"
              item-value="value"
              label="Beste Alternative gemäß folgender Metrik"
              :rules="rules.bestAlternativeMetric"
            ></v-select>

            <div class="mt-4">
              <v-slider
                v-model="bestAlternativeSettings.metric_improvement_percentage"
                :label="`Verbesserung/Verschlechterung: ${bestAlternativeSettings.metric_improvement_percentage} %`"
                min="-100"
                max="100"
                step="5"
                :thumb-label="true"
                color="success"
              ></v-slider>

              <v-checkbox
                v-model="bestAlternativeSettings.enforce_metric_constraints"
                label="Verbesserung/Verschlechterung berücksichtigen"
                hint="Aktivieren um nur Alternativen mit der gewünschten Verbesserung/Verschlechterung zu zeigen"
                persistent-hint
              ></v-checkbox>
            </div>

            <!-- Optional Constraints -->
            <v-text-field
              v-model="bestAlternativeSettings.max_legs"
              label="Max. Anzahl Umstiege (optional)"
              type="number"
              min="0"
              max="10"
              clearable
              hint="Leer lassen für unbegrenzte Umstiege"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col :style="`z-index: auto;`">
            <h3>Standort-Auswahl</h3>
            <v-select
              v-model="selectedProjectId"
              :items="projects"
              item-title="name"
              item-value="id"
              label="Projekt"
            ></v-select>
            <v-select
              v-model="selectedCompanyId"
              :items="companies"
              :item-title="'name'"
              :item-value="'id'"
              label="Firma"
            ></v-select>
            <v-select
              v-model="selectedOfficeId"
              :items="offices"
              :item-title="'office_name'"
              :item-value="'office_id'"
              label="Standort"
            ></v-select>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
    <v-container>
      <v-row justify="space-between">
        <v-btn :disabled="!valid" @click="changeSettings" :loading="loading"
          >Bestätigen</v-btn
        >
        <p>{{ statusMessage }}</p>
        <v-btn @click="tearDownRoutine">Abbrechen</v-btn>
      </v-row>
    </v-container>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapStores } from "pinia";
import { useMapInputStore } from "../store/mapInputStore";
import { useOfficeStore } from "../store/officeStore";
import { Metric } from "../model/metrics";
import { findByKey, resolveOfficeById } from "../utils/office";
import { isNoRouteDataError } from "../utils/errors";
import { toRaw } from "vue";
import {
  BestAlternativeFilters,
  DEFAULT_BEST_ALTERNATIVE_FILTERS,
  TRANSPORT_MODE_OPTIONS,
  METRIC_OPTIONS,
} from "../model/bestAlternativeModels";

/**
 * Handles immediate mapsettings (available in the map-editor)
 * Some of the code is copied from the office-management
 */
export default defineComponent({
  data() {
    return {
      selectedProjectId: null,
      selectedCompanyId: null,
      selectedOfficeId: null,
      transports: [
        {
          name: "Pkw",
          id: "Auto",
        },
        {
          name: "ÖV",
          id: "ÖV",
        },
        {
          name: "E-Auto",
          id: "E-Auto",
        },
        {
          name: "Fahrrad",
          id: "Fahrrad",
        },
        {
          name: "Pedelec",
          id: "Pedelec",
        },
        {
          name: "Taxi",
          id: "Taxi",
        },
        {
          name: "ÖV & Taxi",
          id: "ÖV & Taxi",
        },
        {
          name: "Zu Fuß",
          id: "Zu Fuß",
        },
        {
          name: "Park & Ride",
          id: "Park & Ride",
        },
        {
          name: "Bike & Ride",
          id: "Bike & Ride",
        },
        {
          name: "e-Scooter",
          id: "e-Scooter",
        },
        {
          name: "Carsharing",
          id: "Carsharing",
        },
        {
          name: "Werksbus",
          id: "Werksbus",
        },
        {
          name: "Bike & Werksbus",
          id: "Bike & Werksbus",
        },
        {
          name: "Bahnreaktivierung & Ride",
          id: "Bahnreaktivierung & Ride",
        },
        {
          name: "Park & Ride & Bike",
          id: "Park & Ride & Bike",
        },
      ],
      metrics: [
        {
          name: "CO₂ pro km",
          value: "co2",
        },
        {
          name: "Zeit in min.",
          value: "time_in_mins",
        },
        {
          name: "Schritte pro km",
          value: "sport",
        },
        {
          name: "Kosten in €",
          value: "cost",
        },
        {
          name: "Entfernung in km",
          value: "distance",
        },
      ],
      best_transport_metrics: [
        {
          name: "CO₂ - optimiert",
          value: "co2",
        },
        {
          name: "Zeit - optimiert",
          value: "time_in_mins",
        },
        {
          name: "Bewegungs - optimiert",
          value: "sport",
        },
        {
          name: "Kosten - optimiert",
          value: "cost",
        },
        {
          name: "Nutzen - optimiert",
          value: "distance",
        },
      ],
      firstTransport: undefined as string | undefined,
      secondTransport: undefined as string | undefined,
      metric: undefined as Metric | undefined,
      rules: {
        transport: [
          (val: string | undefined) => {
            if (!val) {
              return false;
            }
            return true;
          },
        ],
        metric: [
          (val: string | undefined) => {
            if (!val) {
              return false;
            }
            return true;
          },
        ],
        bestAlternativeTransport: [
          (val: string | undefined) => {
            if (!val) {
              return "Aktuelles Verkehrsmittel ist erforderlich";
            }
            return true;
          },
        ],
        bestAlternativeMetric: [
          (val: string | undefined) => {
            if (!val) {
              return "Metrik ist erforderlich";
            }
            return true;
          },
        ],
      },
      valid: undefined as boolean | undefined,
      statusMessage: "" as string,
      loading: false,
      prepare_import: false,
      bestAlternativeSettings: {
        ...DEFAULT_BEST_ALTERNATIVE_FILTERS,
      } as BestAlternativeFilters,
      transportModeOptions: TRANSPORT_MODE_OPTIONS,
      metricOptions: METRIC_OPTIONS,
    };
  },
  emits: {
    "set-map-settings": () => {
      return true;
    },
    "cancel-action": () => {
      return true;
    },
    "no-route-data": () => {
      return true;
    },
  },
  props: {
    // in case the settigns should appear on top of the ui
    zIndex: {
      type: Number,
      required: false,
      default: -1,
    },
    /**
     * settingsType must be a value in [0,1,2]
     * You can use the enum MapSettingType.
     * 0: Standard
     * 1: Metric
     * 2: CompareMetric
     */
    settingsType: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  async mounted() {
    await this.setUp();
    if (this.officeStore.selectedProject) {
      this.selectedProjectId = await this.officeStore.selectedProject.id;
    }
    if (this.officeStore.selectedCompany) {
      this.selectedCompanyId = await this.officeStore.selectedCompany.id;
    }
    if (this.officeStore.selectedOffice) {
      this.selectedOfficeId = await this.officeStore.selectedOffice.office_id;
    }
  },
  computed: {
    ...mapStores(useMapInputStore, useOfficeStore),
    offices() {
      // @ts-ignore
      return this.officeStore.getOffices;
    },
    companies() {
      // @ts-ignore
      return this.officeStore.getCompanies;
    },
    projects() {
      // @ts-ignore
      return this.officeStore.getProjects;
    },
    selectedProject() {
      // Check the officeStore for a selectedProject
      let selected = this.officeStore.selectedProject;

      // If there's no selectedProject in the officeStore
      if (!selected) {
        // @ts-ignore
        selected = this.projects.find((val: object) => {
          // @ts-ignore
          if (val.id) {
            // @ts-ignore
            if (val.id === this.selectedProjectId) {
              return true;
            }
          }
          return false;
        });
      }
      if (!selected) {
        return { name: null, id: null };
      }
      return selected;
    },
    selectedCompany() {
      let selected = this.officeStore.selectedCompany;

      if (!selected) {
        // @ts-ignore
        selected = this.companies.find((val: object) => {
          // @ts-ignore
          if (val.id) {
            // @ts-ignore
            if (val.id === this.selectedCompanyId) {
              return true;
            }
          }
          return false;
        });
      }
      // return fallback
      if (!selected) {
        return { name: null, id: null };
      }
      return selected;
    },
    selectedOffice() {
      let selected: object = this.officeStore.selectedOffice;
      if (!selected) {
        // @ts-ignore
        selected = toRaw(this.offices).find((val: object) => {
          // @ts-ignore
          if (val["office_id"]) {
            // @ts-ignore
            if (val["office_id"] === this.selectedOfficeId) {
              return true;
            }
          }
          return false;
        });
      }
      if (!selected) {
        return {
          name: null,
          type: null,
          owner: null,
          street: null,
          house_number: null,
          postal_code: null,
          district: null,
          geo: {
            coordinates: [null, null],
          },
        };
      }
      return selected;
    },
  },
  methods: {
    async setUp() {
      this.firstTransport = undefined;
      this.secondTransport = undefined;
      this.metric = undefined;
      await this.officeStore.fetchProjects();
    },
    async changeSettings() {
      this.loading = true;
      if ([0, 1, 2, 3, 4].includes(this.settingsType)) {
        this.statusMessage = "Übertrage Einträge in internen Speicher";
        if (this.settingsType === 0) {
          this.metric = Metric.DISTANCE;
        }
        if (this.settingsType === 1) {
          this.metric = Metric.TIME;
        }
        this.mapInputStore.setFirstTransport(this.firstTransport!);
        this.mapInputStore.setMetric(this.metric!);
        if (this.settingsType === 2) {
          this.mapInputStore.setSecondTransport(this.secondTransport!);
        }
        if (this.settingsType === 3) {

          this.mapInputStore.setMetric(this.metric!);
        }
        if (this.settingsType === 4) {
          this.mapInputStore.updateBestAlternativeFilters(
            this.bestAlternativeSettings,
          );
        }
      }
      // Commit the CURRENT dropdown selection into the central officeStore.
      // The selectedProject/Company/Office computeds each prefer the (stale)
      // officeStore value over the current dropdown id, and their watchers write
      // that stale value back — so on re-selection the officeStore keeps the old
      // project/company/office triple. Everything reading the store then uses a
      // stale (and, across a project switch, inconsistent) triple: metric layers
      // fail with "No paths found" and the office marker freezes at the old spot,
      // while addresses (fetched from the raw dropdown ids) already moved.
      //
      // changeSettings runs on confirm, when projects/companies/offices are all
      // loaded for the current selection, so resolving by id here is race-free
      // (unlike the watchers, which fire mid-transition).
      const resolvedProject = findByKey(
        toRaw(this.projects),
        "id",
        this.selectedProjectId,
      );
      const resolvedCompany = findByKey(
        toRaw(this.companies),
        "id",
        this.selectedCompanyId,
      );
      const resolvedOffice =
        resolveOfficeById(toRaw(this.offices), this.selectedOfficeId) ??
        // @ts-ignore
        this.selectedOffice;
      // Keep the whole triple consistent so metric layers (getDistanceLayer et
      // al.), the office marker and the addresses all use the same selection.
      if (resolvedProject) this.officeStore.selectProject(resolvedProject);
      if (resolvedCompany) this.officeStore.selectCompany(resolvedCompany);
      // @ts-ignore
      this.officeStore.selectOffice(resolvedOffice);
      // @ts-ignore
      let officeInput = resolvedOffice;
      let office = {
        office_id: this.selectedOfficeId,
        // @ts-ignore
        name: officeInput["office_name"],
        // @ts-ignore
        owner: officeInput["owner"] ? officeInput["owner"] : null,
        // @ts-ignore
        type: officeInput["type"] ? officeInput["type"] : null,
        adress: {
          // @ts-ignore
          street: officeInput["street"],
          // @ts-ignore
          house_number: officeInput["house_number"]
            ? // @ts-ignore
              officeInput["house_number"]
            : null,
          // @ts-ignore
          district: officeInput["district"],
          geo:
            // @ts-ignore
            officeInput["geo"] && officeInput["geo"]["coordinates"]
              ? {
                  // @ts-ignore
                  coordinates: officeInput["geo"]["coordinates"],
                  type: "Point",
                }
              : null,
        },
      };
      // @ts-ignore
      this.mapInputStore.setOffice(office);
      // now load adress and metric data
      this.statusMessage = "Lade Adressen";
      if (
        !(
          this.selectedProjectId &&
          this.selectedCompanyId &&
          this.selectedOfficeId
        )
      ) {
        console.warn("Did not load adresses, because ids not provided.");
        return;
      }
      this.mapInputStore
        .fetchAdresses(
          this.selectedProjectId,
          this.selectedCompanyId,
          this.selectedOfficeId,
        )
        .then((result) => {
          if (
            this.settingsType === 0 ||
            this.settingsType === 3 ||
            this.settingsType === 4
          ) {
            this.finishDataFetchingRoutine();
            return result;
          }
          this.statusMessage = "Lade Metriken für Haupt-Transportmittel";
          // Return the inner promises so a rejection (e.g. no route data for the
          // office) propagates to the .catch below instead of becoming an
          // unhandled rejection that leaves the loading spinner stuck.
          return this.mapInputStore
            .fetchMetricsFirst(
              this.selectedProjectId!,
              this.selectedCompanyId!,
              this.selectedOfficeId!,
            )
            .then((metricsResult) => {
              if (this.settingsType === 1) {
                this.finishDataFetchingRoutine();
                return metricsResult;
              }
              this.statusMessage =
                "Lade Metriken für Vergleichs-Transportmittel";
              return this.mapInputStore
                .fetchMetricsSecond(
                  this.selectedProjectId!,
                  this.selectedCompanyId!,
                  this.selectedOfficeId!,
                )
                .then((metricsResultSecond) => {
                  this.finishDataFetchingRoutine();
                  return metricsResultSecond;
                });
            });
        })
        .catch((error) => {
          // Reset the loading state so the user stays in the settings and can
          // pick another office. On "no route data" ask the map editor to show
          // the hint dialog; otherwise log the unexpected error.
          this.loading = false;
          this.statusMessage = "";
          if (isNoRouteDataError(error)) {
            this.$emit("no-route-data");
          } else {
            console.error("Error while loading map settings data:", error);
          }
        });
    },
    finishDataFetchingRoutine() {
      this.loading = false;
      this.statusMessage = "";
      this.$emit("set-map-settings");
    },
    async tearDownRoutine() {
      // This routine will most likely be activated, if something with fetching data goes wrong.
      // It will give the user a chance to start over and re-run the routine.
      this.loading = false;
      this.statusMessage = "";
      this.firstTransport = undefined;
      this.secondTransport = undefined;
      this.metric = undefined;
      this.bestAlternativeSettings = { ...DEFAULT_BEST_ALTERNATIVE_FILTERS };
      this.mapInputStore.$reset();
      this.officeStore.$reset();
      this.officeStore.fetchProjects();
      this.$emit("cancel-action");
    },
    resetBestAlternativeSettings() {
      this.bestAlternativeSettings = { ...DEFAULT_BEST_ALTERNATIVE_FILTERS };
    },
    async calculateBestAlternatives() {
      this.loading = true;
      this.statusMessage = "Berechne beste Alternativen...";
      try {
        await this.mapInputStore.updateBestAlternativeFilters(
          this.bestAlternativeSettings,
        );
        this.finishDataFetchingRoutine();
      } catch (error) {
        console.error("Best Alternative calculation failed:", error);
        this.statusMessage = "Fehler bei der Berechnung";
        this.loading = false;
      }
    },
  },
  watch: {
    async selectedProjectId(newVal, oldVal) {
      if (newVal != oldVal) {
        await this.officeStore.fetchCompanies(newVal);
      }
      const project = this.selectedProject;
      if (project) {
        this.officeStore.selectProject(project);
      }
    },
    async selectedCompanyId(newVal, oldVal) {
      if (newVal != oldVal && this.selectedProjectId) {
        await this.officeStore.fetchOffices(this.selectedProjectId, newVal);
      }
      const company = this.selectedCompany;
      if (company) {
        this.officeStore.selectCompany(company);
      }
    },
    async selectedOfficeId(newVal, oldVal) {
      if (newVal != oldVal) {
        await this.officeStore.fetchShifts(newVal);
      }
      const office = this.selectedOffice;
      if (office) {
        this.officeStore.selectOffice(office);
      }
    },
  },
});
</script>

<style scoped>
h3 {
  margin-bottom: 10px;
}
.container-style {
  height: 600px;
  width: 400px;
}
</style>
